import React, { FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import cogoToast from '@successtar/cogo-toast'
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai'
import dreamBoothIcon from '/assets/images/dreambooth-white.png'
import klakklikklukIcon from '/assets/klakklikkluk/logo.png'
import { useAuthStore } from '../stores/authStore'
import { useTranslation } from 'react-i18next'
import { LanguageSelector, MfaLoginPrompt } from '@renderer/components'
import { getPreloadApi } from '@renderer/lib/runtime'

const Login: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [error, setError] = useState<string>()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showMfaPrompt, setShowMfaPrompt] = useState(false)
  const [mfaEmail, setMfaEmail] = useState('')
  const [mfaSessionId, setMfaSessionId] = useState('')
  const login = useAuthStore((state) => state.login)
  const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle)
  const initializeCookie = useAuthStore((state) => state.initializeCookie)
  const isKlakklikkluk = window.location.hostname.includes('klakklikkluk')

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()

    try {
      const result = await login(email, password)

      // Check if MFA is required
      if (result.mfaRequired) {
        setMfaEmail(result.email || email)
        setMfaSessionId(result.tempSessionId || '')
        setShowMfaPrompt(true)
        cogoToast.info(<span>{t('login.mfaRequired', 'MFA verification required')}</span>)
        return
      }

      // Normal login success
      cogoToast.success(<span>{t('login.successMessage', 'Successfully logged in!')}</span>)
      const callbackUrl = new URLSearchParams(window.location.search).get('callbackUrl') || '/'
      navigate(callbackUrl)
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('login.networkError', 'Network error. Please try again later.')
      cogoToast.error(<span>{errorMessage}</span>)
      setError(errorMessage)
    }
  }

  const handleMfaSuccess = async (): Promise<void> => {
    setShowMfaPrompt(false)
    cogoToast.success(<span>{t('login.successMessage', 'Successfully logged in!')}</span>)
    const callbackUrl = new URLSearchParams(window.location.search).get('callbackUrl') || '/'
    navigate(callbackUrl)
  }

  const handleMfaCancel = (): void => {
    setShowMfaPrompt(false)
    setError(undefined)
  }

  return (
    <div className="bg-gradient-to-b from-black via-black/90 to-black/70 flex items-center justify-center px-4" style={{ minHeight: '100dvh' }}>
      <div className="w-full max-w-md">
        <img
          src={isKlakklikkluk ? klakklikklukIcon : dreamBoothIcon}
          alt={t('login.logoAlt', 'DreamBooth Logo')}
          width={200}
          height={50}
          className="mx-auto mb-4"
        />
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-whiteout to-gray-400">
            {t('login.welcomeBack', 'Welcome Back')}
          </h2>
          <p className="text-gray-400 mt-2">{t('login.signInPrompt', 'Sign in to your account')}</p>
        </div>

        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 shadow-xl">
          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <input
                type="email"
                name="email"
                required
                placeholder={t('login.email', 'Email')}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blueSparkle text-whiteout placeholder-gray-400"
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder={t('login.password', 'Password')}
                required
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blueSparkle text-whiteout placeholder-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-label={
                  showPassword
                    ? t('login.hidePassword', 'Hide password')
                    : t('login.showPassword', 'Show password')
                }
              >
                {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
              </button>
            </div>

            {error && <p className="text-red-500">{error}</p>}

            <button
              type="submit"
              className="w-full py-3 font-semibold text-white rounded-lg bg-blueSparkle hover:opacity-90 transition duration-300"
              style={{ touchAction: 'manipulation' }}
            >
              {t('login.loginButton', 'Login')}
            </button>

            {typeof getPreloadApi()?.startGoogleOAuth === 'function' && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 text-gray-400">{t('login.or', 'OR')}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await loginWithGoogle('login')
                      // Initialize cookie in session after OAuth login
                      await initializeCookie()
                      // Small delay to ensure cookie is set before navigation
                      await new Promise((resolve) => setTimeout(resolve, 100))
                      cogoToast.success(
                        <span>{t('login.successMessage', 'Successfully logged in!')}</span>
                      )
                      const callbackUrl =
                        new URLSearchParams(window.location.search).get('callbackUrl') || '/'
                      navigate(callbackUrl)
                    } catch (error: unknown) {
                      const errorMessage =
                        error instanceof Error
                          ? error.message
                          : t('login.networkError', 'Network error. Please try again later.')
                      cogoToast.error(<span>{errorMessage}</span>)
                      setError(errorMessage)
                    }
                  }}
                  className="w-full py-3 font-semibold text-white rounded-lg bg-white/10 border border-gray-600 hover:bg-white/20 transition duration-300 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {t('login.continueWithGoogle', 'Continue with Google')}
                </button>
              </>
            )}

            <p className="text-center text-gray-400">
              <a
                href={`${isKlakklikkluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL}/dashboard`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purpleSnail hover:text-whiteout transition-colors"
              >
                {t('login.visitDashboard', 'Visit the web dashboard')}
              </a>
              <br />
              <span className="text-sm text-gray-500">
                {t('login.accountManagement', 'For account management, sign up, and more options')}
              </span>
            </p>
          </form>
        </div>
      </div>

      {showMfaPrompt && (
        <MfaLoginPrompt
          email={mfaEmail}
          tempSessionId={mfaSessionId}
          onSuccess={handleMfaSuccess}
          onCancel={handleMfaCancel}
        />
      )}
    </div>
  )
}

export default Login
